// "What can Debbie access?"
//
// This page exists to answer that without a ticket, and to answer its inverse,
// which nothing in the portal could answer before: who else can reach a given
// company's data. Both come from one request, because they are the same facts read
// in two directions.
//
// The reason the question was hard is that a folder mapping hides its own effect.
// A folder called `acme-inbound` in Debbie's profile may point anywhere in the
// bucket, and Acme's own page says nothing about it. So the two things this page
// insists on showing for every folder are where it actually resolves to and whose
// data that is.
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  getAccessMap,
  type AccessFolder,
  type AccessMap,
  type AccessUser,
  type Exposure,
} from '../api';

type View = 'by-user' | 'by-owner' | 'all-folders';

export function AdminAccessPage() {
  const [map, setMap] = useState<AccessMap>();
  const [error, setError] = useState<string>();
  const [view, setView] = useState<View>('by-user');
  // `?who=` lets the roster and a user's own page link straight to the answer for
  // one partner, which is the path somebody handling a ticket actually takes. It
  // seeds the box rather than locking the filter, so the next question ("and who
  // else is in there?") is one edit away.
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get('who') ?? '');

  useEffect(() => {
    void (async () => {
      try {
        setMap(await getAccessMap());
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, []);

  if (error) return <p className="error">{error}</p>;
  if (!map) return <p>Loading…</p>;

  return (
    <section>
      <header className="page-header">
        <div>
          <h1>Who can reach what</h1>
          <p className="hint">
            Every folder each SFTP user has, and the bucket prefix it really resolves to. This is
            read from the same profiles the SFTP session policies are built from, so it is what the
            server enforces rather than a separate record of it.
          </p>
        </div>
      </header>

      <Summary summary={map.summary} />

      <div className="segmented">
        <button
          type="button"
          className={view === 'by-user' ? 'active' : undefined}
          onClick={() => setView('by-user')}
        >
          What a user can reach
        </button>
        <button
          type="button"
          className={view === 'by-owner' ? 'active' : undefined}
          onClick={() => setView('by-owner')}
        >
          Who can reach a company
        </button>
        <button
          type="button"
          className={view === 'all-folders' ? 'active' : undefined}
          onClick={() => setView('all-folders')}
        >
          Every folder
        </button>
      </div>

      <label>
        Search
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="debbie"
          spellCheck={false}
        />
        <span className="hint">{SEARCH_HINT[view]}</span>
      </label>

      {view === 'by-user' && <ByUser users={map.users} query={query} />}
      {view === 'by-owner' && <ByOwner exposures={map.exposures} query={query} />}
      {view === 'all-folders' && <AllFolders users={map.users} query={query} />}
    </section>
  );
}

// Per view, because the same box filters different things in each and a hint that
// described only one of them would be worse than none.
const SEARCH_HINT: Record<View, string> = {
  'by-user': 'Matches the user, or any company and prefix they can reach.',
  'by-owner': 'Matches the company whose data it is, or whoever reaches it.',
  'all-folders': 'Matches the user, the folder name, the prefix, or the owner.',
};

// Headline numbers, so nobody has to read every row to find out whether anything
// here needs attention. Only the counts that can indicate a problem are
// emphasised; users and folders are context.
function Summary({ summary }: { summary: AccessMap['summary'] }) {
  const { shares, writableShares, wholeHomeShares, wildcardShares, unattributedShares } = summary;
  return (
    <div className="card wide">
      <dl>
        <dt>SFTP users</dt>
        <dd>
          {summary.users} with {summary.folders} folders between them
        </dd>
        <dt>Cross-company access</dt>
        <dd>
          {shares === 0 ? (
            'None — every folder stays inside its own user’s home directory.'
          ) : (
            <>
              {shares} {shares === 1 ? 'folder reaches' : 'folders reach'} another company’s data
              {writableShares > 0 && <>, {writableShares} of them with write access</>}
              {wholeHomeShares > 0 && (
                <>
                  {' '}
                  — {wholeHomeShares} {wholeHomeShares === 1 ? 'grants' : 'grant'} a whole home
                  directory rather than one folder
                </>
              )}
              .
            </>
          )}
        </dd>
        {wildcardShares > 0 && (
          <>
            <dt className="error">Wildcards</dt>
            <dd className="error">
              {wildcardShares} stored {wildcardShares === 1 ? 'prefix contains' : 'prefixes contain'}{' '}
              <code>*</code> or <code>?</code>. Those are ordinary characters in an S3 key but
              wildcards in the IAM policy the prefix becomes, so the folder grants more than it
              names. New ones are now rejected; these were saved before that.
            </dd>
          </>
        )}
        {unattributedShares > 0 && (
          <>
            <dt>Unowned prefixes</dt>
            <dd>
              {unattributedShares} {unattributedShares === 1 ? 'folder points' : 'folders point'} at
              a prefix that is not inside any user’s home directory. Legitimate, but no partner would
              ever notice the access exists.
            </dd>
          </>
        )}
      </dl>
    </div>
  );
}

// ---- What a user can reach ---------------------------------------------------

function ByUser({ users, query }: { users: AccessUser[]; query: string }) {
  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return users;
    return users.filter(
      (user) =>
        user.username.toLowerCase().includes(needle) ||
        user.folders.some(
          (folder) =>
            folder.prefix.toLowerCase().includes(needle) ||
            (folder.owner ?? '').toLowerCase().includes(needle)
        )
    );
  }, [users, query]);

  if (users.length === 0) return <p className="hint">No SFTP users yet.</p>;
  if (matches.length === 0) return <p className="hint">Nobody matches “{query}”.</p>;

  return (
    <>
      {matches.map((user) => {
        const crossing = user.folders.filter((folder) => folder.reachesAnother);
        return (
          <div className="card wide" key={user.username}>
            <header className="page-header">
              <div>
                <h2>
                  <Link to={`/users/${encodeURIComponent(user.username)}`}>{user.username}</Link>
                </h2>
                {/* The one-line answer, so a ticket can be closed from this
                    sentence alone without reading the table under it. */}
                <p className="hint">
                  Home <code>{user.homeDirectory}</code>.{' '}
                  {crossing.length === 0
                    ? 'Reaches nothing outside it.'
                    : `Also reaches ${listOwners(crossing)}.`}
                </p>
                {user.comment && <p className="hint">{user.comment}</p>}
              </div>
            </header>

            {user.folders.length === 0 ? (
              <p className="hint">No folders — this user can sign in but will see nothing.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>They see</th>
                    <th>Which is</th>
                    <th>Whose</th>
                    <th>Access</th>
                  </tr>
                </thead>
                <tbody>
                  {user.folders.map((folder) => (
                    <tr key={folder.path}>
                      <td>
                        <code>{folder.path}</code>
                      </td>
                      <td>
                        <code className="wrap">{folder.prefix}</code>
                        {folder.comment && <p className="hint">{folder.comment}</p>}
                      </td>
                      <td>
                        <Owner folder={folder} self={user.username} />
                      </td>
                      <td>
                        <Access folder={folder} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </>
  );
}

// "Acme Health and two others" reads better than a list of six in a sentence, and
// the table below has the detail anyway.
const listOwners = (folders: AccessFolder[]): string => {
  const owners = [...new Set(folders.map((folder) => folder.owner ?? 'a prefix nobody owns'))];
  if (owners.length === 1) return owners[0];
  if (owners.length === 2) return `${owners[0]} and ${owners[1]}`;
  return `${owners[0]} and ${owners.length - 1} others`;
};

function Owner({ folder, self }: { folder: AccessFolder; self: string }) {
  if (folder.owner === null) {
    return <span className="badge warn">nobody</span>;
  }
  if (!folder.reachesAnother) {
    return <span className="hint">their own</span>;
  }
  return (
    <>
      <Link to={`/users/${encodeURIComponent(folder.owner)}`}>{folder.owner}</Link>
      {folder.owner === self ? null : <span className="badge warn">another company</span>}
    </>
  );
}

// Permission plus the two things that make a share bigger than it looks. Both are
// stated in words rather than symbols, because this is the text somebody will
// paste into a ticket reply.
function Access({ folder }: { folder: AccessFolder }) {
  return (
    <>
      <span className={folder.write ? 'badge warn' : 'badge'}>{PERMISSION[folder.permission]}</span>
      {folder.wholeHome && <p className="warn">their whole home directory, not one folder</p>}
      {folder.wildcard && (
        <p className="error">
          contains a wildcard — grants more than the prefix names
        </p>
      )}
    </>
  );
}

const PERMISSION: Record<string, string> = {
  'read-write': 'read + write',
  'read-only': 'read only',
  'write-only': 'write only',
};

// ---- Every folder ------------------------------------------------------------
//
// The same facts as the by-user view, flattened to one row per folder. Worth
// having as its own view rather than a tidier version of the first one: the
// per-user cards answer "what can this one person reach", and cannot answer
// "which folders across everyone are writable", because the rows that would have
// to be compared live in different tables. Flat and sortable, they can be.
//
// It is also the view that survives being pasted into a ticket or a spreadsheet,
// which is what the questions that started this page turn into.

type Row = AccessFolder & { username: string };

type Column = 'user' | 'folder' | 'prefix' | 'owner' | 'access';

const COLUMNS: Array<{ column: Column; label: string }> = [
  { column: 'user', label: 'User' },
  { column: 'folder', label: 'Folder' },
  { column: 'prefix', label: 'Resolves to' },
  { column: 'owner', label: 'Whose' },
  { column: 'access', label: 'Access' },
];

const SORT_KEY: Record<Column, (row: Row) => string | number> = {
  user: (row) => row.username,
  folder: (row) => row.path,
  prefix: (row) => row.prefix,
  // Unowned prefixes sort together at one end instead of under whatever letter
  // "null" would have landed on.
  owner: (row) => row.owner ?? '￿',
  // Ranked by what it grants, not by the label's spelling: alphabetically
  // "read + write" and "read only" sort adjacent and "write only" lands last,
  // which looks sorted and orders nothing anyone cares about. Write outranks read
  // because changing a partner's data is the more consequential of the two, and
  // "which of these can write?" is the question this column gets asked.
  access: (row) => (row.write ? 2 : 0) + (row.read ? 1 : 0),
};

const compareRows =
  ({ column, descending }: Sort) =>
  (a: Row, b: Row) => {
    const left = SORT_KEY[column](a);
    const right = SORT_KEY[column](b);
    const order =
      typeof left === 'number' && typeof right === 'number'
        ? left - right
        : String(left).localeCompare(String(right));
    // Ties break on user then folder, and that tiebreak is never reversed, so a
    // sort is reproducible rather than dependent on the order DynamoDB happened
    // to return the profiles in.
    if (order === 0) return a.username.localeCompare(b.username) || a.path.localeCompare(b.path);
    return descending ? -order : order;
  };

interface Sort {
  column: Column;
  descending: boolean;
}

function AllFolders({ users, query }: { users: AccessUser[]; query: string }) {
  // Defaults to the order the by-user view shows, so switching between the two
  // does not rearrange rows underneath someone mid-question.
  const [sort, setSort] = useState<Sort>({ column: 'user', descending: false });

  const rows = useMemo<Row[]>(
    () =>
      users.flatMap((user) =>
        user.folders.map((folder) => ({ ...folder, username: user.username }))
      ),
    [users]
  );

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered =
      needle === ''
        ? rows
        : rows.filter((row) =>
            [row.username, row.path, row.prefix, row.owner ?? ''].some((field) =>
              field.toLowerCase().includes(needle)
            )
          );
    return [...filtered].sort(compareRows(sort));
  }, [rows, query, sort]);

  if (rows.length === 0) return <p className="hint">No folders yet.</p>;

  return (
    <>
      <table>
        <thead>
          <tr>
            {COLUMNS.map(({ column, label }) => (
              <SortHeader
                key={column}
                column={column}
                label={label}
                sort={sort}
                onSort={(next) =>
                  setSort((current) =>
                    // Clicking the active column reverses it; clicking a new one
                    // starts ascending, which is the direction a first click means.
                    current.column === next
                      ? { column: next, descending: !current.descending }
                      : { column: next, descending: false }
                  )
                }
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {matches.length === 0 ? (
            <tr>
              <td colSpan={COLUMNS.length} className="hint">
                No folder matches “{query}”.
              </td>
            </tr>
          ) : (
            matches.map((row) => (
              <tr key={`${row.username}/${row.path}`}>
                <td>
                  <Link to={`/users/${encodeURIComponent(row.username)}`}>{row.username}</Link>
                </td>
                <td>
                  <code>{row.path}</code>
                  {row.comment && <p className="hint">{row.comment}</p>}
                </td>
                <td>
                  <code className="wrap">{row.prefix}</code>
                </td>
                <td>
                  <Owner folder={row} self={row.username} />
                </td>
                <td>
                  <Access folder={row} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <p className="hint">
        {matches.length === rows.length
          ? `${rows.length} ${rows.length === 1 ? 'folder' : 'folders'} across ${users.length} ${
              users.length === 1 ? 'user' : 'users'
            }.`
          : `${matches.length} of ${rows.length} folders.`}{' '}
        Click a column heading to sort by it.
      </p>
    </>
  );
}

function SortHeader({
  column,
  label,
  sort,
  onSort,
}: {
  column: Column;
  label: string;
  sort: Sort;
  onSort: (column: Column) => void;
}) {
  const active = sort.column === column;
  return (
    // aria-sort is what tells a screen reader the table is sorted and by which
    // column; the arrow only says so visually.
    <th aria-sort={active ? (sort.descending ? 'descending' : 'ascending') : 'none'}>
      <button type="button" className="sort" onClick={() => onSort(column)}>
        {label}
        <span aria-hidden="true">{active ? (sort.descending ? ' ↓' : ' ↑') : ''}</span>
      </button>
    </th>
  );
}

// ---- Who can reach a company -------------------------------------------------

function ByOwner({ exposures, query }: { exposures: Exposure[]; query: string }) {
  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return exposures;
    return exposures.filter(
      (exposure) =>
        (exposure.owner ?? '').toLowerCase().includes(needle) ||
        exposure.reachableBy.some((entry) => entry.username.toLowerCase().includes(needle))
    );
  }, [exposures, query]);

  // Not an empty state to apologise for: it is the answer everyone hopes for.
  if (exposures.length === 0) {
    return (
      <p className="ok">
        No company’s data is reachable by anyone else. Every folder stays inside its own user’s home
        directory.
      </p>
    );
  }
  if (matches.length === 0) return <p className="hint">Nothing matches “{query}”.</p>;

  return (
    <>
      {matches.map((exposure) => (
        <div className="card wide" key={exposure.owner ?? ' unowned'}>
          <h2>
            {exposure.owner === null ? (
              'Prefixes nobody owns'
            ) : (
              <Link to={`/users/${encodeURIComponent(exposure.owner)}`}>{exposure.owner}</Link>
            )}
          </h2>
          <p className="hint">
            {exposure.owner === null
              ? 'These prefixes are not inside any SFTP user’s home directory, so no partner would ever see that they are being read or written.'
              : `Reachable by ${exposure.reachableBy.length} other ${
                  exposure.reachableBy.length === 1 ? 'user' : 'users'
                }.`}
          </p>
          <table>
            <thead>
              <tr>
                <th>Reached by</th>
                <th>Through their folder</th>
                <th>Prefix</th>
                <th>Access</th>
              </tr>
            </thead>
            <tbody>
              {exposure.reachableBy.map((entry) => (
                <tr key={`${entry.username}/${entry.via}`}>
                  <td>
                    <Link to={`/users/${encodeURIComponent(entry.username)}`}>{entry.username}</Link>
                  </td>
                  <td>
                    {/* The alias, because removing the access means editing this
                        folder on this user -- not anything on the owner. */}
                    <code>{entry.via}</code>
                    {entry.comment && <p className="hint">{entry.comment}</p>}
                  </td>
                  <td>
                    <code className="wrap">{entry.prefix}</code>
                  </td>
                  <td>
                    <span className={entry.write ? 'badge warn' : 'badge'}>
                      {PERMISSION[entry.permission]}
                    </span>
                    {entry.wholeHome && <p className="warn">the whole home directory</p>}
                    {entry.wildcard && <p className="error">contains a wildcard</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </>
  );
}
